import { Injectable } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../core/api.service';
import { SyncCryptService } from '../../../core/crypt/sync-crypt.service';
import { LoggerService } from '../../../core/logger.service';
import { UrlService } from '../../../core/url.service';
import { ERROR_CODES, ErrCode } from '../../../shared/models';
import { ITransferItemDownload } from 'src/sync';
import {
  DOWNLOAD_CHUNK_HOST_ROTATION_DELAY_MS,
  DOWNLOAD_CHUNK_MAX_RETRIES_ON_TIMEOUT,
  DOWNLOAD_CHUNK_MAX_RETRIES_PER_HOST,
  DOWNLOAD_CHUNK_RETRY_BASE_MS,
  DOWNLOAD_CHUNK_RETRY_JITTER_RATIO,
  DOWNLOAD_CHUNK_RETRY_MAX_MS,
} from '../../transfer.constants';

export interface DownloadChunkFetchOptions {
  cancel$?: Observable<void>;
  isCancelled?: () => boolean;
}

const TERMINAL_DOWNLOAD_ERROR_CODES: ReadonlySet<number> = new Set([
  ERROR_CODES.FILE_UNAVAILABLE,
  ERROR_CODES.FILE_CORRUPTED,
  ERROR_CODES.DOWNLOAD_CANCELLED,
  ERROR_CODES.DOWNLOAD_INVALID_RESPONSE,
  ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR,
]);

export function isRetryableDownloadError(error: unknown): boolean {
  if (error instanceof ErrCode) {
    return !TERMINAL_DOWNLOAD_ERROR_CODES.has(error.code);
  }
  return true;
}

/**
 * Single implementation of "fetch one encrypted chunk's bytes" for every secure-download path.
 * Owns host selection, retry policy, backoff and the transfer item's progress reporting; callers are
 * left responsible only for decrypting what it returns.
 */
@Injectable({
  providedIn: 'root',
})
export class DownloadChunkFetchService {
  constructor(
    private api: ApiService,
    private crypt: SyncCryptService,
    private log: LoggerService,
    private url: UrlService,
  ) {}

  /**
   * Retries a transient failure on the same host with exponential backoff + jitter, then rotates to
   * the next download host, until every host is exhausted. Terminal errors fail immediately.
   */
  public async fetchChunk(
    tItem: ITransferItemDownload,
    offset: number,
    reqLength: number,
    startHostIndex: number,
    options?: DownloadChunkFetchOptions,
  ): Promise<ArrayBuffer> {
    const progressHighWaterMark = { value: this.chunkBaselineBytes(offset) };
    let lastError: unknown;

    // The start index is always attempted, even when downloadhosts is unpopulated (thumbnail path).
    for (
      let hostIndex = startHostIndex;
      hostIndex === startHostIndex || this.url.downloadhosts[hostIndex];
      hostIndex++
    ) {
      for (let attempt = 0; attempt <= DOWNLOAD_CHUNK_MAX_RETRIES_PER_HOST; attempt++) {
        if (options?.isCancelled?.()) {
          throw new ErrCode(ERROR_CODES.DOWNLOAD_CANCELLED);
        }

        try {
          return await this.fetchChunkOnce(tItem, offset, reqLength, hostIndex, progressHighWaterMark, options);
        } catch (error) {
          lastError = error;
          this.log.e('Error fetching download chunk', error);

          if (!this.isRetryable(error)) {
            throw error;
          }

          if (attempt >= this.maxRetriesFor(error)) {
            break;
          }

          const waitMilliseconds = this.retryBackoffMs(attempt);
          this.log.warn(
            `Retrying chunk on host ${this.url.downloadhosts[hostIndex]} in ${waitMilliseconds}ms (attempt ${
              attempt + 1
            })`,
          );
          await this.delay(waitMilliseconds);

          if (options?.isCancelled?.()) {
            throw new ErrCode(ERROR_CODES.DOWNLOAD_CANCELLED);
          }
        }
      }

      if (this.url.downloadhosts[hostIndex + 1]) {
        this.log.warn(`Retrying download on host ${this.url.downloadhosts[hostIndex + 1]}`);
        await this.delay(DOWNLOAD_CHUNK_HOST_ROTATION_DELAY_MS);
      }
    }

    this.log.error('All download hosts exhausted, rejecting chunk');
    throw lastError ?? new ErrCode(ERROR_CODES.DOWNLOAD_INVALID_RESPONSE);
  }

  /** Exponential backoff (capped) with jitter for chunk retries. */
  public retryBackoffMs(attempt: number): number {
    const backoff = Math.min(DOWNLOAD_CHUNK_RETRY_BASE_MS * 2 ** attempt, DOWNLOAD_CHUNK_RETRY_MAX_MS);
    const jitter = Math.floor(Math.random() * backoff * DOWNLOAD_CHUNK_RETRY_JITTER_RATIO);
    return backoff + jitter;
  }

  private fetchChunkOnce(
    tItem: ITransferItemDownload,
    offset: number,
    reqLength: number,
    hostIndex: number,
    progressHighWaterMark: { value: number },
    options?: DownloadChunkFetchOptions,
  ): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      let settled = false;
      const request = options?.cancel$
        ? this.api.fetchFile(tItem, offset, reqLength, hostIndex).pipe(takeUntil(options.cancel$))
        : this.api.fetchFile(tItem, offset, reqLength, hostIndex);

      request.subscribe({
        next: (resp) => {
          if (resp.type === HttpEventType.DownloadProgress) {
            this.reportProgress(tItem, offset, resp.loaded, progressHighWaterMark);
          } else if (resp.type === HttpEventType.Response) {
            settled = true;
            resolve(resp.body as ArrayBuffer);
          }
        },
        error: (err) => reject(err),
        complete: () => {
          // takeUntil completes the stream on cancellation; without this the promise would never settle.
          if (!settled) {
            reject(new ErrCode(ERROR_CODES.DOWNLOAD_CANCELLED));
          }
        },
      });
    });
  }

  /** Progress is clamped to a per-chunk high-water mark so a retry cannot walk the bar backwards. */
  private reportProgress(
    tItem: ITransferItemDownload,
    offset: number,
    loaded: number,
    progressHighWaterMark: { value: number },
  ): void {
    const bytesSent = loaded + offset;
    const gcmTags = Math.ceil(bytesSent / this.crypt.GCM_PACKET_SIZE) * this.crypt.GCM_TAG_OVERHEAD;
    const plaintextBytesSent = bytesSent - gcmTags;

    if (plaintextBytesSent <= progressHighWaterMark.value) {
      return;
    }
    progressHighWaterMark.value = plaintextBytesSent;

    tItem.bytes_sent = plaintextBytesSent;
    let percent = Math.round((tItem.bytes_sent / tItem.filesize) * 100 * 100) / 100;
    if (percent > 100) {
      percent = 100;
    }
    tItem.progress_percent = percent;
  }

  private chunkBaselineBytes(offset: number): number {
    const gcmTags = Math.ceil(offset / this.crypt.GCM_PACKET_SIZE) * this.crypt.GCM_TAG_OVERHEAD;
    return Math.max(offset - gcmTags, 0);
  }

  private maxRetriesFor(error: unknown): number {
    if (error instanceof ErrCode && error.code === ERROR_CODES.DOWNLOAD_TIMEOUT) {
      return DOWNLOAD_CHUNK_MAX_RETRIES_ON_TIMEOUT;
    }
    return DOWNLOAD_CHUNK_MAX_RETRIES_PER_HOST;
  }

  private isRetryable(error: unknown): boolean {
    return isRetryableDownloadError(error);
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }
}
